import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ValidationErrors from '@/components/validation-errors';
import FieldInput from '@/pages/apps/records/components/field-input';
import {
  moneyProblem,
  toInput,
  toSubmitted,
  type InputValue,
} from '@/pages/apps/records/components/field-values';
import {
  app_customers_path,
  app_list_record_path,
  app_list_records_path,
  edit_app_list_record_path,
  new_app_list_record_path,
} from '@/rails/routes';
import type { AppList, AppRecord, AppShell, Customer } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

export interface FieldError {
  key: string;
  message: string;
}

interface RecordFormProps {
  app: AppShell;
  list: AppList;
  record: AppRecord;
  customers: Customer[];
  errors: string[];
  fieldErrors: FieldError[];
}

const WIDE_TYPES = ['long_text', 'location'];

// The form for one record of a list. It builds an input for each field the list
// defines, and saves a new record or an existing one depending on whether the
// record has an id yet. Attachments are added on the saved record, not here.
const RecordForm = ({ app, list, record, customers, errors, fieldErrors }: RecordFormProps) => {
  const fields = list.fields.filter((field) => field.type !== 'attachments');
  const [inputs, setInputs] = useState<Record<string, InputValue>>(() =>
    Object.fromEntries(
      fields.map((field) => [
        field.key,
        toInput(field, record.values.find((pair) => pair.key === field.key)?.value),
      ])
    )
  );
  const [localProblems, setLocalProblems] = useState<FieldError[]>([]);
  const [saving, setSaving] = useState(false);
  const recordsPath = app_list_records_path(app.id, list.key);
  const formPath = record.id
    ? edit_app_list_record_path(app.id, list.key, record.id)
    : new_app_list_record_path(app.id, list.key);

  const problemFor = (key: string): string | undefined => {
    const problem =
      localProblems.find((candidate) => candidate.key === key) ??
      fieldErrors.find((candidate) => candidate.key === key);

    return problem?.message;
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const problems = fields.flatMap((field) => {
      const message = moneyProblem(field, inputs[field.key]);

      return message ? [{ key: field.key, message }] : [];
    });
    setLocalProblems(problems);

    if (problems.length === 0) {
      // Pairs, not an object keyed by field: a field key must reach the server
      // exactly as the list defines it.
      const payload = {
        record: {
          values: fields.map((field) => ({
            key: field.key,
            value: toSubmitted(field, inputs[field.key]),
          })),
        },
      };
      const options = {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
        // A refused save is rendered at the address it was sent to, which is
        // not a page of its own. Put the form's address back, so a refresh or
        // an upload that returns here still lands on this form.
        onError: () => router.replace({ url: formPath, preserveState: true, preserveScroll: true }),
      };

      if (record.id) {
        router.patch(app_list_record_path(app.id, list.key, record.id), payload, options);
      } else {
        router.post(recordsPath, payload, options);
      }
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <ValidationErrors errors={errors} />

      <Card>
        <CardContent>
          <div className="grid gap-5 sm:grid-cols-2">
            {fields.map((field) => (
              <div
                key={field.key}
                className={WIDE_TYPES.includes(field.type) ? 'sm:col-span-2' : undefined}
              >
                <FieldInput
                  field={field}
                  id={`field-${field.key}`}
                  value={inputs[field.key]}
                  onChange={(value) => setInputs((current) => ({ ...current, [field.key]: value }))}
                  error={problemFor(field.key)}
                  customers={customers}
                  customersHref={app_customers_path(app.id)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={saving}>
          {record.id ? `Save ${list.singular.toLowerCase()}` : `Add ${list.singular.toLowerCase()}`}
        </Button>
        <Button asChild variant="ghost">
          <Link href={recordsPath}>
            {record.id ? `Back to ${list.name.toLowerCase()}` : 'Cancel'}
          </Link>
        </Button>
      </div>
    </form>
  );
};

export default RecordForm;
